import React, { useState, useEffect } from 'react'
import { useFormik } from "formik";
import * as yup from 'yup';
import { userRequest } from '../../utils/requestMethods';
import { useStateContext } from '../../contexts/ContextProvider';
import ButtonLoading from '../ButtonLoading';
import Alert from '../common/Alert';
import { FiMapPin, FiNavigation, FiMap, FiGlobe } from 'react-icons/fi';

const addressSchema = yup.object().shape({
    address_line: yup.string().required("Street address is required"),
    city: yup.string().required("City is required"),
    state: yup.string().required("State is required"),
    zip_code: yup.string()
        .matches(/^[0-9]{5,}$/, "Postal code must be at least 5 digits")
        .required("Postal code is required"),
});

const STATES = [
    "ABIA", "ADAMAWA", "AKWA_IBOM", "ANAMBRA", "BAUCHI", "BAYELSA", "BENUE", "BORNO",
    "CROSS_RIVER", "DELTA", "EBONYI", "EDO", "EKITI", "ENUGU", "FCT", "GOMBE", "IMO",
    "JIGAWA", "KADUNA", "KANO", "KATSINA", "KEBBI", "KOGI", "KWARA", "LAGOS", "NASARAWA",
    "NIGER", "OGUN", "ONDO", "OSUN", "OYO", "PLATEAU", "RIVERS", "SOKOTO", "TARABA", "YOBE", "ZAMFARA"
];

const Address = ({ address, setRefresh, onSaveSuccess }) => {
    const { school } = useStateContext();
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            address_line: "",
            city: "",
            state: "",
            zip_code: "",
        },
        validationSchema: addressSchema,
        onSubmit: (values) => registerHandler(values),
    });

    useEffect(() => {
        if (address) {
            const src = address.registered || address.main || {};
            formik.setValues({
                address_line: src.address_line || "",
                city: src.city || "",
                state: src.state || "",
                zip_code: src.zip_code || "",
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [address]);

    const registerHandler = async (values) => {
        setLoading(true);
        try {
            const addressPayload = {
                address_line: values.address_line,
                address_line2: values.address_line,
                city: values.city,
                state: values.state,
                zip_code: values.zip_code,
            };
            await userRequest.put(`/schools/${school._id}`, {
                address: {
                    registered: addressPayload,
                    main: addressPayload,
                },
            });

            setSuccess("School address updated successfully");
            setTimeout(() => {
                setSuccess("");
                setRefresh("address");
                onSaveSuccess?.();
            }, 2000);
        } catch (error) {
            console.log(error);
            setError(error.response?.data || "Something went wrong.");
            setTimeout(() => setError(""), 5000);
        } finally {
            setLoading(false);
        }
    };

    const currentAddress = address?.registered || address?.main;

    return (
        <div className="max-w-4xl mx-auto">
            <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">Where is your school located?</h3>
                <p className="text-gray-500 max-w-2xl">
                    Enter your school's address. This will be used for compliance and correspondence purposes.
                </p>
            </div>

            {/* Current address summary */}
            {currentAddress?.address_line && (
                <div className="mb-10 bg-white border border-gray-200 rounded-2xl shadow-sm p-6 dark:bg-gray-800 dark:border-gray-700">
                    <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <span className="w-2 h-6 bg-indigo-500 rounded-full"></span>
                        Current Address
                    </h4>
                    <div className="space-y-3">
                        <div className="flex items-start gap-3">
                            <div className="mt-1 text-gray-400"><FiNavigation /></div>
                            <div>
                                <p className="text-xs text-gray-500">Street Address</p>
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{currentAddress.address_line}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="mt-1 text-gray-400"><FiMap /></div>
                            <div>
                                <p className="text-xs text-gray-500">City & State</p>
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    {currentAddress.city}, {currentAddress.state?.replace(/_/g, ' ')}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="mt-1 text-gray-400"><FiGlobe /></div>
                            <div>
                                <p className="text-xs text-gray-500">Postal Code</p>
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{currentAddress.zip_code}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-2xl">
                <h4 className="text-xl font-bold mb-6 text-gray-800">
                    {currentAddress?.address_line ? 'Update Address' : 'Add School Address'}
                </h4>

                {success && <Alert type="success" message={success} />}
                {error && <Alert type="error" message={error} />}

                <form onSubmit={formik.handleSubmit} className="space-y-6">
                    <div>
                        <h5 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                            <FiMapPin className="text-indigo-500" /> School Address
                        </h5>
                        <div className="grid grid-cols-1 gap-6">
                            <div>
                                <label htmlFor="address_line" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Street Address</label>
                                <input
                                    type="text"
                                    id="address_line"
                                    name="address_line"
                                    value={formik.values.address_line}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    placeholder="Street address, P.O. box, etc."
                                    className="block w-full rounded-xl bg-gray-50 border-gray-200 focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 transition-all text-sm py-2.5 px-4"
                                />
                                {formik.errors.address_line && formik.touched.address_line &&
                                    <p className="mt-1 text-xs text-red-500">{formik.errors.address_line}</p>}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">City</label>
                                    <input
                                        type="text"
                                        id="city"
                                        name="city"
                                        value={formik.values.city}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        className="block w-full rounded-xl bg-gray-50 border-gray-200 focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 transition-all text-sm py-2.5 px-4"
                                    />
                                    {formik.errors.city && formik.touched.city &&
                                        <p className="mt-1 text-xs text-red-500">{formik.errors.city}</p>}
                                </div>
                                <div>
                                    <label htmlFor="state" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">State</label>
                                    <select
                                        id="state"
                                        name="state"
                                        value={formik.values.state}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        className="block w-full rounded-xl bg-gray-50 border-gray-200 focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 transition-all text-sm py-2.5 px-4"
                                    >
                                        <option value="">Select state</option>
                                        {STATES.map(s => (
                                            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                                        ))}
                                    </select>
                                    {formik.errors.state && formik.touched.state &&
                                        <p className="mt-1 text-xs text-red-500">{formik.errors.state}</p>}
                                </div>
                                <div>
                                    <label htmlFor="zip_code" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Postal Code</label>
                                    <input
                                        type="text"
                                        id="zip_code"
                                        name="zip_code"
                                        value={formik.values.zip_code}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        className="block w-full rounded-xl bg-gray-50 border-gray-200 focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 transition-all text-sm py-2.5 px-4"
                                    />
                                    {formik.errors.zip_code && formik.touched.zip_code &&
                                        <p className="mt-1 text-xs text-red-500">{formik.errors.zip_code}</p>}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="pt-4 flex justify-end">
                        {loading ? (
                            <ButtonLoading styles="inline-flex items-center justify-center py-2.5 px-8 border border-transparent text-sm font-medium rounded-full text-white bg-indigo-400 cursor-wait" />
                        ) : (
                            <button
                                type="submit"
                                className="inline-flex items-center justify-center py-2.5 px-8 border border-transparent shadow-md text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all"
                            >
                                Save Address
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    )
}

export default Address
